import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { protectSheetWithRandomPassword } from "../ExcelUtils";


export async function exportBookEntryList({
    data = [],
    columns = [], // 👈 dynamic columns
    fileName = "BookEntryList.xlsx",
    sheetName = "Book Entries",
    selectedDate,
    title,
    loginPlaceId
}) {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sheetName);

    // ===== Format Date =====
    function formatDate(date) {
        if (!date) return "";
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    const formattedRange =
        selectedDate?.length === 2
            ? `${formatDate(selectedDate[0])} to ${formatDate(selectedDate[1])}`
            : selectedDate?.length === 1
                ? formatDate(selectedDate[0])
                : "";

    // ===== Heading =====
    const heading = sheet.addRow([
        (title ? `${title}` : "") +
        (formattedRange ? ` - ${formattedRange}` : "")
    ]);

    sheet.mergeCells(1, 1, 1, columns.length);
    heading.font = { bold: true, size: 16, color: { argb: "FFFFFF" } };
    heading.alignment = { horizontal: "center", vertical: "middle" };
    heading.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "305496" }
    };

    // ===== Printed Date =====
    const printed = sheet.addRow([
        `Printed Date: ${new Date().toLocaleString()}`
    ]);
    sheet.mergeCells(2, 1, 2, columns.length);
    printed.font = { italic: true };
    printed.alignment = { horizontal: "left" };
    printed.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "D9E1F2" }
    };

    sheet.addRow([]);

    // ===== Header =====
    const headers = columns.map(col => col.header);
    const headerRow = sheet.addRow(headers);

    headerRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: "FFFFFF" } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "4472C4" }
        };
        cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" }
        };
    });

    // ===== Column widths =====
    sheet.columns = columns.map(col => ({
        width: col.width || 20
    }));

    // ===== Data Rows =====
    data.forEach((item, index) => {
        const row = sheet.addRow(
            columns.map(col => {
                let value = item[col.key];

                if (col.type === "date" && value) {
                    return new Date(value);
                }

                return value ?? "";
            })
        );

        row.eachCell((cell, colNum) => {
            const col = columns[colNum - 1];

            // Date format
            if (col.type === "date" && cell.value) {
                cell.numFmt = "yyyy-mm-dd";
                cell.alignment = { horizontal: "center" };
            }

            // Alignment
            if (col.align) {
                cell.alignment = {
                    ...(cell.alignment || {}),
                    horizontal: col.align
                };
            }

            // Wrap text
            if (col.wrap) {
                cell.alignment = {
                    ...(cell.alignment || {}),
                    wrapText: true,
                    vertical: "top"
                };
            }

            // Currency format (optional)
            if (col.type === "currency") {
                cell.numFmt = "#,##0.00";
                cell.alignment = { horizontal: "right" };
            }

            // Borders
            cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" }
            };

            // Zebra striping
            if (index % 2 === 0) {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "F7F9FC" }
                };
            }
        });
    });

    // ===== Auto Filter =====
    sheet.autoFilter = {
        from: { row: headerRow.number, column: 1 },
        to: { row: headerRow.number, column: columns.length }
    };
    if (loginPlaceId === "1") {
        const password = protectSheetWithRandomPassword(sheet, 12);
        console.log(`Sheet protected with password: ${password}`);
    }

    // ===== Freeze Header =====
    sheet.views = [
        { state: "frozen", ySplit: headerRow.number }
    ];

    // ===== Export =====
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });

    saveAs(blob, fileName);
}